import React, { useState, useEffect } from "react";
import { getProfile, updateProfile } from "../../services/authService";
import "../../index.css";
import { toast } from "react-toastify";

export default function SchoolInfo() {

  const [form, setForm] = useState({
    schoolName: "",
    addr: "",
    phone: "",
    email: "",
    gst: "",
    accn: "",
    ifsc: "",
    regdno: "",
    web: "",
    logo_url: "",
    logoFile: null,
  });

  const [errors, setErrors] = useState({});

  // FETCH PROFILE DATA
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await getProfile();
      console.log("Profile Response:", response);
      toast.success(response.message, {
        toastId: "profile-success",
      });

      const profile = response.data;
      setForm({
        schoolName: profile.name || "",
        addr: profile.addr || "",
        phone: profile.phone || "",
        email: profile.email || "",
        gst: profile.gst || "",
        accn: profile.accn || "",
        ifsc: profile.ifsc || "",
        regdno: profile.regdno || "",
        web: profile.web || "",
        logo_url: profile.logo_url || "",
        logoFile: null,
      });
    } catch (error) {
      console.error("Profile Error:", error);
      toast.error("Failed to fetch profile", {
        toastId: "profile-error",
      });
    }
  };

  // HANDLE INPUT CHANGE
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // FILE INPUT
    if (name === "logoFile") {
      const file = files[0];
      if (file) {
        const allowedTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
        ];

        // FILE TYPE VALIDATION
        if (!allowedTypes.includes(file.type)) {
          setErrors({
            ...errors,
            logoFile: "Only JPG, JPEG, PNG allowed",
          });

          return;
        }

        // FILE SIZE VALIDATION (2MB)
        if (file.size > 2 * 1024 * 1024) {
          setErrors({
            ...errors,
            logoFile: "Image size must be less than 2MB",
          });
          return;
        }

        // IMAGE PREVIEW
        setForm({
          ...form,
          logoFile: file,
          logo_url: URL.createObjectURL(file),
        });
        setErrors({
          ...errors,
          logoFile: "",
        });
      }

      return;
    }

    // NORMAL INPUTS
    setForm({
      ...form,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  // VALIDATION
  const validate = () => {
    let newErrors = {};

    // SCHOOL NAME
    if (!form.schoolName.trim()) {
      newErrors.schoolName = "School name is required";
    }

    // ADDRESS
    if (!form.addr.trim()) {
      newErrors.addr = "Address is required";
    }

    // PHONE
    if (!form.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^[0-9]{10}$/.test(form.phone)) {
      newErrors.phone = "Enter valid 10 digit phone number";
    }

    // EMAIL
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)
    ) {
      newErrors.email = "Enter valid email";
    }

    // GST
    if (!form.gst.trim()) {
      newErrors.gst = "GST is required";
    }

    // ACCOUNT NUMBER
    if (!form.accn.trim()) {
      newErrors.accn = "Account number is required";
    }

    // IFSC
    if (!form.ifsc.trim()) {
      newErrors.ifsc = "IFSC is required";
    }

    // REGD NO
    if (!form.regdno.trim()) {
      newErrors.regdno = "Registration number is required";
    }

    // WEBSITE
    if (
      form.web &&
      !/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i.test(form.web)
    ) {
      newErrors.web = "Enter valid website";
    }

    // LOGO
    if (!form.logo_url) {
      newErrors.logoFile = "Logo is required";
    }

    return newErrors;
  };

  // FORM SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try {
      const payload = new FormData();
      payload.append("name", form.schoolName);
      payload.append("addr", form.addr);
      payload.append("phone", form.phone);
      payload.append("email", form.email);
      payload.append("gst", form.gst);
      payload.append("accn", form.accn);
      payload.append("ifsc", form.ifsc);
      payload.append("regdno", form.regdno);
      payload.append("web", form.web);

      // FILE
      if (form.logoFile) {
        payload.append("logo", form.logoFile);
      }
      console.log("Update Payload:", payload);
      const response = await updateProfile(payload);
      console.log("Update Response:", response);
      toast.success(
        response?.message || "Profile updated successfully!",
        {
          toastId: "profile-update-success",
        }
      );

      // REFRESH PROFILE
      fetchProfile();
    } catch (error) {
      console.error("Update Error:", error);
      toast.error(
        error?.response?.data?.message ||
        "Failed to update profile",
        {
          toastId: "profile-update-error",
        }
      );
    }
  };

  return (
    <>
      <style>{`
        .page {
          background: #f4f6fb;
          min-height: 100vh;
          padding: 20px;
        }

        .card {
          max-width: 1400px;
          margin: auto;
          background: #fff;
          border-radius: 10px;
          padding: 45px 50px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }

        .header {
          font-size: 22px;
          font-weight: 600;
          margin-bottom: 30px;
          color: #0b1a4a;
        }

        .grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 25px;
        }

        .field {
          display: flex;
          flex-direction: column;
        }

        .field label {
          font-size: 14px;
          margin-bottom: 6px;
          font-weight: 500;
        }

        .field input {
          padding: 12px;
          border-radius: 6px;
          border: 1px solid #cbd5e1;
          font-size: 14px;
          outline: none;
        }

        .field input:focus {
          border-color: #2563eb;
        }

        .field input.error {
          border-color: red;
        }

        .error-text {
          color: red;
          font-size: 12px;
          margin-top: 4px;
        }

        .logo-preview {
          width: 120px;
          height: 120px;
          object-fit: cover;
          margin-top: 10px;
          border-radius: 6px;
          border: 1px solid #d1d5db;
        }

        .actions {
          margin-top: 30px;
          display: flex;
          justify-content: flex-end;
        }

        .btn {
          background: #2563eb;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .btn:hover {
          background: #1d4ed8;
        }

        @media (max-width: 768px) {

          .grid {
            grid-template-columns: 1fr;
          }

          .card {
            padding: 25px;
          }
        }
      `}</style>

      <div className="page">
        <div className="card">
          <div className="header">
            School Information
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid">

              {/* SCHOOL NAME */}
              <div className="field">
                <label>School Name</label>
                <input
                  type="text"
                  name="schoolName"
                  value={form.schoolName}
                  onChange={handleChange}
                  className={errors.schoolName ? "error" : ""}
                />
                {errors.schoolName && (
                  <span className="error-text">
                    {errors.schoolName}
                  </span>
                )}
              </div>

              {/* ADDRESS */}
              <div className="field">
                <label>Address</label>
                <input
                  type="text"
                  name="addr"
                  value={form.addr}
                  onChange={handleChange}
                  className={errors.addr ? "error" : ""}
                />
                {errors.addr && (
                  <span className="error-text">
                    {errors.addr}
                  </span>
                )}
              </div>

              {/* PHONE */}
              <div className="field">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className={errors.phone ? "error" : ""}
                />
                {errors.phone && (
                  <span className="error-text">
                    {errors.phone}
                  </span>
                )}
              </div>

              {/* EMAIL */}
              <div className="field">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className={errors.email ? "error" : ""}
                />
                {errors.email && (
                  <span className="error-text">
                    {errors.email}
                  </span>
                )}
              </div>

              {/* GST */}
              <div className="field">
                <label>GST</label>
                <input
                  type="text"
                  name="gst"
                  value={form.gst}
                  onChange={handleChange}
                  className={errors.gst ? "error" : ""}
                />
                {errors.gst && (
                  <span className="error-text">
                    {errors.gst}
                  </span>
                )}
              </div>

              {/* ACCOUNT NUMBER */}
              <div className="field">
                <label>Account Number</label>
                <input
                  type="text"
                  name="accn"
                  value={form.accn}
                  onChange={handleChange}
                  className={errors.accn ? "error" : ""}
                />
                {errors.accn && (
                  <span className="error-text">
                    {errors.accn}
                  </span>
                )}
              </div>

              {/* IFSC */}
              <div className="field">
                <label>IFSC</label>
                <input
                  type="text"
                  name="ifsc"
                  value={form.ifsc}
                  onChange={handleChange}
                  className={errors.ifsc ? "error" : ""}
                />
                {errors.ifsc && (
                  <span className="error-text">
                    {errors.ifsc}
                  </span>
                )}
              </div>

              {/* REGD NO */}
              <div className="field">
                <label>Registration No</label>
                <input
                  type="text"
                  name="regdno"
                  value={form.regdno}
                  onChange={handleChange}
                  className={errors.regdno ? "error" : ""}
                />
                {errors.regdno && (
                  <span className="error-text">
                    {errors.regdno}
                  </span>
                )}
              </div>

              {/* WEBSITE */}
              <div className="field">
                <label>Website</label>
                <input
                  type="text"
                  name="web"
                  value={form.web}
                  onChange={handleChange}
                  className={errors.web ? "error" : ""}
                />
                {errors.web && (
                  <span className="error-text">
                    {errors.web}
                  </span>
                )}
              </div>

              {/* LOGO */}
              <div className="field">
                <label>Logo</label>
                <input
                  type="file"
                  name="logoFile"
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={handleChange}
                  className={errors.logoFile ? "error" : ""}
                />
                {errors.logoFile && (
                  <span className="error-text">
                    {errors.logoFile}
                  </span>
                )}

                {/* IMAGE PREVIEW */}
                {form.logo_url && (
                  <img
                    src={form.logo_url}
                    alt="School Logo"
                    className="logo-preview"
                  />
                )}
              </div>
            </div>

            <div className="actions">
              <button
                type="submit"
                className="btn"
              >
                Save Information
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}